import {useEffect, useState} from 'react';
import {Table, Tbody, Td, Th, Thead, Tr} from '@patternfly/react-table';
import {Link, useSearchParams} from 'react-router-dom';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  MenuToggleElement,
  PageSection,
  PanelFooter,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import {SyncIcon} from '@patternfly/react-icons';
import TeamsViewToolbar from './TeamsViewToolbar';
import TeamViewKebab from './TeamViewKebab';
import {ITeams, useDeleteTeam, useFetchTeams} from 'src/hooks/UseTeams';
import {TeamsRoleDropDown} from './TeamsRoleDropDown';
import {BulkDeleteModalTemplate} from 'src/components/modals/BulkDeleteModalTemplate';
import {BulkOperationError, addDisplayError} from 'src/resources/ErrorHandling';
import ErrorModal from 'src/components/errors/ErrorModal';
import {getTeamMemberPath} from 'src/routes/NavigationPath';
import {AlertVariant, useUI} from 'src/contexts/UIContext';
import SetRepoPermissionForTeamModal from 'src/routes/OrganizationsList/Organization/Tabs/TeamsAndMembership/TeamsView/SetRepoPermissionsModal/SetRepoPermissionForTeamModal';
import {ToolbarPagination} from 'src/components/toolbar/ToolbarPagination';
import Conditional from 'src/components/empty/Conditional';
import DeleteModalForRowTemplate from 'src/components/modals/DeleteModalForRowTemplate';
import {usePaginatedSortableTable} from '../../../../../../hooks/usePaginatedSortableTable';
import {useQuayConfig} from 'src/hooks/UseQuayConfig';

export const teamViewColumnNames = {
  teamName: 'Team name',
  members: 'Members',
  repositories: 'Repositories',
  teamRole: 'Team role',
};

export default function TeamsViewList(props: TeamsViewListProps) {
  const config = useQuayConfig();
  const {
    teams: allTeams,
    isLoadingTeams,
    error,
    search,
    setSearch,
  } = useFetchTeams(props.organizationName);

  const searchFilter =
    search.query !== ''
      ? (team: ITeams) =>
          team.name.toLowerCase().includes(search.query.toLowerCase())
      : undefined;

  const {
    paginatedData: paginatedTeams,
    filteredData: filteredTeams,
    getSortableSort,
    paginationProps,
  } = usePaginatedSortableTable(allTeams || [], {
    columns: {
      1: (item: ITeams) => item.name, // Team name
      2: (item: ITeams) => item.member_count, // Members count
      3: (item: ITeams) => item.repo_count, // Repositories count
      4: (item: ITeams) => item.role, // Team role
    },
    filter: searchFilter,
    initialPerPage: 20,
    initialSort: {columnIndex: 1, direction: 'asc'}, // Default sort: Team name ascending
  });

  const [selectedTeams, setSelectedTeams] = useState<ITeams[]>([]);
  const [isKebabOpen, setKebabOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [err, setIsError] = useState<string[]>();
  const [searchParams] = useSearchParams();
  const {addAlert} = useUI();
  const [isSetRepoPermModalOpen, setIsSetRepoPermModalOpen] = useState(false);
  const [repoPermForTeam, setRepoPermForTeam] = useState<string>('');
  const [isDeleteModalForRowOpen, setIsDeleteModalForRowOpen] = useState(false);
  const [teamToBeDeleted, setTeamToBeDeleted] = useState<ITeams>();

  useEffect(() => {
    if (error) {
      addAlert({variant: AlertVariant.Failure, title: `Could not load teams`});
    }
  }, [error]);

  const handleDeleteModalToggle = () => {
    setKebabOpen(!isKebabOpen);
    setDeleteModalIsOpen(!deleteModalIsOpen);
  };

  const kebabItems = [
    <DropdownItem key="delete" onClick={handleDeleteModalToggle}>
      Delete
    </DropdownItem>,
  ];

  /* Mapper object used to render bulk delete table
    - keys are actual column names of the table
    - value is an object type with a "label" which maps to the attributes of <T>
      and an optional "transformFunc" which can be used to modify the value being displayed */
  const mapOfColNamesToTableData = {
    'Team Name': {
      label: 'name',
      transformFunc: (team: ITeams) => {
        return `${team.name}`;
      },
    },
    Members: {
      label: 'members',
      transformFunc: (team: ITeams) => team.member_count,
    },
    Repositories: {
      label: 'repositories',
      transformFunc: (team: ITeams) => {
        return `${team.repo_count}`;
      },
    },
    'Team Role': {
      label: 'team role',
      transformFunc: (team: ITeams) => (
        <Dropdown
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle ref={toggleRef} id="toggle-disabled" isDisabled>
              {team.role}
            </MenuToggle>
          )}
          isOpen={false}
        >
          <DropdownList>
            <DropdownItem>{team.role}</DropdownItem>
          </DropdownList>
        </Dropdown>
      ),
    },
  };

  const {removeTeam: bulkRemoveTeams} = useDeleteTeam({
    orgName: props.organizationName,
    onSuccess: () => {
      setDeleteModalIsOpen(!deleteModalIsOpen);
      setSelectedTeams([]);
      addAlert({
        variant: AlertVariant.Success,
        title: `Successfully deleted teams`,
      });
    },
    onError: (err) => {
      if (err instanceof BulkOperationError) {
        const errMessages = [];
        err.getErrors().forEach((error, team) => {
          addAlert({
            variant: AlertVariant.Failure,
            title: `Could not delete team ${team}: ${error.error}`,
          });
          errMessages.push(
            addDisplayError(`Failed to delete teams ${team}`, error.error),
          );
        });
        setIsError(errMessages);
      } else {
        setIsError([addDisplayError('Failed to delete teams', err)]);
      }
      setDeleteModalIsOpen(!deleteModalIsOpen);
      setSelectedTeams([]);
    },
  });

  const deleteModal = (
    <BulkDeleteModalTemplate
      mapOfColNamesToTableData={mapOfColNamesToTableData}
      handleModalToggle={handleDeleteModalToggle}
      handleBulkDeletion={bulkRemoveTeams}
      isModalOpen={deleteModalIsOpen}
      selectedItems={allTeams?.filter((team) =>
        selectedTeams.some((selected) => team.name === selected.name),
      )}
      resourceName={'teams'}
    />
  );

  const onSelectTeam = (
    team: ITeams,
    rowIndex: number,
    isSelecting: boolean,
  ) => {
    setSelectedTeams((prevSelected) => {
      const otherSelectedTeams = prevSelected.filter(
        (t) => t.name !== team.name,
      );
      return isSelecting ? [...otherSelectedTeams, team] : otherSelectedTeams;
    });
  };

  const setRepoPermModal = (
    <SetRepoPermissionForTeamModal
      isModalOpen={isSetRepoPermModalOpen}
      handleModalToggle={() =>
        setIsSetRepoPermModalOpen(!isSetRepoPermModalOpen)
      }
      organizationName={props.organizationName}
      teamName={repoPermForTeam}
    />
  );

  const {removeTeam} = useDeleteTeam({
    orgName: props.organizationName,
    onSuccess: () => {
      setIsDeleteModalForRowOpen(false);
      addAlert({
        variant: AlertVariant.Success,
        title: `Successfully deleted team: ${teamToBeDeleted.name}`,
      });
    },
    onError: (err) => {
      addAlert({
        variant: AlertVariant.Failure,
        title: `Unable to delete team: ${teamToBeDeleted.name}, ${err}`,
      });
    },
  });

  const deleteRowModal = (
    <DeleteModalForRowTemplate
      deleteMsgTitle={'Remove team from organization'}
      isModalOpen={isDeleteModalForRowOpen}
      toggleModal={() => setIsDeleteModalForRowOpen(!isDeleteModalForRowOpen)}
      deleteHandler={removeTeam}
      itemToBeDeleted={teamToBeDeleted}
      keyToDisplay="name"
    />
  );

  const openSetRepoPermModal = (teamName: string) => {
    setRepoPermForTeam(teamName);
    setIsSetRepoPermModalOpen(!isSetRepoPermModalOpen);
  };

  const showSyncColumn =
    config?.features?.TEAM_SYNCING &&
    config?.config?.AUTHENTICATION_TYPE !== 'Database';

  if (isLoadingTeams) {
    return <Spinner />;
  }

  return (
    <>
      <PageSection hasBodyWrapper={false}>
        <ErrorModal
          title="Team deletion failed"
          error={err}
          setError={setIsError}
        />
        <TeamsViewToolbar
          selectedTeams={selectedTeams}
          deSelectAll={() => setSelectedTeams([])}
          allItems={filteredTeams}
          paginatedItems={paginatedTeams}
          onItemSelect={onSelectTeam}
          page={paginationProps.page}
          setPage={paginationProps.setPage}
          perPage={paginationProps.perPage}
          setPerPage={paginationProps.setPerPage}
          search={search}
          setSearch={setSearch}
          searchOptions={[teamViewColumnNames.teamName]}
          isKebabOpen={isKebabOpen}
          setKebabOpen={setKebabOpen}
          kebabItems={kebabItems}
          deleteKebabIsOpen={deleteModalIsOpen}
          deleteModal={deleteModal}
          isSetRepoPermModalOpen={isSetRepoPermModalOpen}
          setRepoPermModal={setRepoPermModal}
          handleModalToggle={props.handleModalToggle}
          isReadOnly={props.isReadOnly}
          isAdmin={props.isAdmin}
        />
        {props.children}
        <Conditional if={isDeleteModalForRowOpen}>{deleteRowModal}</Conditional>
        <Table aria-label="Selectable table" variant="compact">
          <Thead>
            <Tr>
              <Th />
              <Conditional if={showSyncColumn}>
                <Th modifier="center" />
              </Conditional>
              <Th sort={getSortableSort(1)}>{teamViewColumnNames.teamName}</Th>
              <Th sort={getSortableSort(2)}>{teamViewColumnNames.members}</Th>
              <Th sort={getSortableSort(3)}>
                {teamViewColumnNames.repositories}
              </Th>
              <Th sort={getSortableSort(4)}>{teamViewColumnNames.teamRole}</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {paginatedTeams?.map((team, rowIndex) => (
              <Tr key={rowIndex}>
                <Td
                  select={{
                    rowIndex,
                    onSelect: (_event, isSelecting) =>
                      onSelectTeam(team, rowIndex, isSelecting),
                    isSelected: selectedTeams.some((t) => t.name === team.name),
                  }}
                />
                <Conditional if={showSyncColumn}>
                  <Td modifier="center">
                    {team.is_synced && (
                      <Tooltip content="Team is synchronized with a backing group">
                        <SyncIcon data-test-id="sync-icon" />
                      </Tooltip>
                    )}
                  </Td>
                </Conditional>
                <Td dataLabel={teamViewColumnNames.teamName}>
                  {team.can_view ? (
                    <Link
                      to={getTeamMemberPath(
                        location.pathname,
                        props.organizationName,
                        team.name,
                        searchParams.get('tab'),
                      )}
                    >
                      {team.name}
                    </Link>
                  ) : (
                    team.name
                  )}
                </Td>
                <Td
                  dataLabel={teamViewColumnNames.members}
                  data-testid={`member-count-for-${team.name}`}
                >
                  {team.can_view ? (
                    <Link
                      to={getTeamMemberPath(
                        location.pathname,
                        props.organizationName,
                        team.name,
                        searchParams.get('tab'),
                      )}
                    >
                      {team.member_count}
                    </Link>
                  ) : (
                    team.member_count
                  )}
                </Td>
                <Td dataLabel={teamViewColumnNames.repositories}>
                  {team.can_view ? (
                    <Link
                      to="#"
                      onClick={() => {
                        openSetRepoPermModal(team.name);
                      }}
                    >
                      {team.repo_count}
                    </Link>
                  ) : (
                    team.repo_count
                  )}
                </Td>
                <Td dataLabel={teamViewColumnNames.teamRole}>
                  <TeamsRoleDropDown
                    organizationName={props.organizationName}
                    teamName={team.name}
                    teamRole={team.role}
                    isReadOnly={props.isReadOnly}
                    isAdmin={props.isAdmin}
                  />
                </Td>
                <Conditional if={props.isAdmin && !props.isReadOnly}>
                  <Td data-label="kebab">
                    <TeamViewKebab
                      organizationName={props.organizationName}
                      team={team}
                      deSelectAll={() => setSelectedTeams([])}
                      onSelectRepo={() => {
                        openSetRepoPermModal(team.name);
                      }}
                      onDeleteTeam={() => {
                        setTeamToBeDeleted(team);
                        setIsDeleteModalForRowOpen(!isDeleteModalForRowOpen);
                      }}
                    />
                  </Td>
                </Conditional>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <PanelFooter>
          <ToolbarPagination
            itemsList={filteredTeams}
            perPage={paginationProps.perPage}
            page={paginationProps.page}
            setPage={paginationProps.setPage}
            setPerPage={paginationProps.setPerPage}
            bottom={true}
          />
        </PanelFooter>
      </PageSection>
    </>
  );
}

interface TeamsViewListProps {
  organizationName: string;
  children?: React.ReactNode;
  handleModalToggle: () => void;
  isReadOnly: boolean;
  isAdmin: boolean;
}
